import { useEffect, useRef, useCallback } from 'react';
import type { IFormSchema, IFormStep } from '~/types';

interface UseAutoAdvanceOptions {
  schema: IFormSchema;
  currentStepConfig: IFormStep | undefined;
  formData: Record<string, unknown>;
  onAdvance: () => void;
  enabled?: boolean;
}

interface UseAutoAdvanceResult {
  isAutoAdvancing: boolean;
  cancelAutoAdvance: () => void;
}

export const useAutoAdvance = ({
  schema,
  currentStepConfig,
  formData,
  onAdvance,
  enabled = true,
}: UseAutoAdvanceOptions): UseAutoAdvanceResult => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isAutoAdvancingRef = useRef(false);
  const previousDataRef = useRef<Record<string, unknown>>({});

  const cancelAutoAdvance = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isAutoAdvancingRef.current = false;
  }, []);

  // Check if a field is auto-advanceable (radio, single-select, boolean)
  const isAutoAdvanceableField = useCallback(
    (fieldName: string): boolean => {
      const fieldSchema = schema.properties[fieldName] as Record<string, unknown> | undefined;
      if (!fieldSchema) return false;

      const type = fieldSchema.type as string;
      const hasEnum = Array.isArray(fieldSchema.enum);
      const uiWidget = (schema.uiSchema?.[fieldName] as Record<string, unknown>)?.['ui:widget'];

      // Radio buttons or single-select dropdowns
      if (type === 'string' && hasEnum) {
        return true;
      }

      // Boolean toggle/checkbox
      if (type === 'boolean') {
        return true;
      }

      // Explicit radio widget
      if (uiWidget === 'radio') {
        return true;
      }

      return false;
    },
    [schema.properties, schema.uiSchema],
  );

  // Check if all step fields are filled
  const areAllStepFieldsFilled = useCallback((): boolean => {
    if (!currentStepConfig) return false;

    for (const fieldName of currentStepConfig.fields) {
      const value = formData[fieldName];
      if (value === undefined || value === null || value === '') {
        return false;
      }
    }
    return true;
  }, [currentStepConfig, formData]);

  // Check if step should auto-advance on field change
  const shouldAutoAdvanceOnFieldChange = useCallback((): boolean => {
    if (!currentStepConfig?.autoAdvance?.enabled) return false;
    if (currentStepConfig.autoAdvance.trigger && currentStepConfig.autoAdvance.trigger !== 'fieldChange') {
      return false;
    }

    // Check if all fields in this step are auto-advanceable
    for (const fieldName of currentStepConfig.fields) {
      if (!isAutoAdvanceableField(fieldName)) {
        return false;
      }
    }

    return true;
  }, [currentStepConfig, isAutoAdvanceableField]);

  // Detect field changes and trigger auto-advance
  useEffect(() => {
    if (!enabled || !currentStepConfig) return;

    // Check if any field in the current step changed
    const stepFields = currentStepConfig.fields;
    let hasChange = false;

    for (const fieldName of stepFields) {
      if (formData[fieldName] !== previousDataRef.current[fieldName]) {
        hasChange = true;
        break;
      }
    }

    previousDataRef.current = { ...formData };

    if (!hasChange) return;

    // Only auto-advance if configured and all fields are filled
    if (shouldAutoAdvanceOnFieldChange() && areAllStepFieldsFilled()) {
      const delay = currentStepConfig.autoAdvance?.delay ?? 500;

      cancelAutoAdvance();
      isAutoAdvancingRef.current = true;

      timeoutRef.current = setTimeout(() => {
        isAutoAdvancingRef.current = false;
        onAdvance();
      }, delay);
    }
  }, [
    enabled,
    currentStepConfig,
    formData,
    shouldAutoAdvanceOnFieldChange,
    areAllStepFieldsFilled,
    cancelAutoAdvance,
    onAdvance,
  ]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      cancelAutoAdvance();
    };
  }, [cancelAutoAdvance]);

  return {
    isAutoAdvancing: isAutoAdvancingRef.current,
    cancelAutoAdvance,
  };
};
